import { Box, Typography, Button, List, ListItem, ListItemText } from '@mui/material'
import { COLORS } from '../theme'
import {
  stepHeadingSx,
  stepDescriptionSx,
  primaryButtonSx,
  outlinedButtonSx,
} from '../styles'
import type { PageEntry } from '../hooks/useBatchScrape'

interface SlowPagesNoticeProps {
  /** Pages still being read. */
  pages: PageEntry[]
  onKeepWaiting: () => void
  onContinue: () => void
}

export default function SlowPagesNotice({
  pages,
  onKeepWaiting,
  onContinue,
}: SlowPagesNoticeProps) {
  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', py: 4, px: 2 }}>
      <Typography variant="h5" sx={stepHeadingSx}>
        This is taking longer than usual
      </Typography>
      <Typography variant="body1" sx={stepDescriptionSx}>
        We’re still reading {pages.length === 1 ? 'this page' : 'these pages'}.
        You can keep waiting, or build your profile now from everything else you
        gave us.
      </Typography>

      <List disablePadding sx={{ mb: 3 }}>
        {pages.map((page) => (
          <ListItem
            key={page.url}
            disableGutters
            sx={{ borderBottom: `1px solid ${COLORS.border}`, py: 1 }}
          >
            <ListItemText
              primary={page.title || page.url}
              secondary={page.title ? page.url : undefined}
              primaryTypographyProps={{
                variant: 'body2',
                sx: { overflowWrap: 'anywhere' },
              }}
              secondaryTypographyProps={{ sx: { overflowWrap: 'anywhere' } }}
            />
          </ListItem>
        ))}
      </List>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Button
          disableRipple
          variant="contained"
          onClick={onKeepWaiting}
          sx={primaryButtonSx}
        >
          Keep waiting
        </Button>
        <Button
          disableRipple
          variant="outlined"
          onClick={onContinue}
          sx={outlinedButtonSx}
        >
          Continue without them
        </Button>
      </Box>

      <Typography variant="body2" sx={{ color: COLORS.secondary }}>
        {pages.length === 1
          ? 'If it finishes later, you can update your profile from it on your profile page after you complete onboarding.'
          : 'If they finish later, you can update your profile from them on your profile page after you complete onboarding.'}
      </Typography>
    </Box>
  )
}
